import cookie from 'js-cookie';
import { jwtDecode, InvalidTokenError } from 'jwt-decode';
import FetchHelper from '@di/fetch-helper';
import { isDefined } from '@di/js-utils';

const DEFAULT_DOMAIN = ".di.no";
const SSO_ISSUER = "DI";
const AUTH_CODE_SEARCH_PARAM = "authCode";
const authEnvironments = [
  "local",
  "development",
  "staging",
  "production"
];
const loginUrls = {
  local: "https://auth.dev.foundations.di.no",
  development: "https://auth.dev.foundations.di.no",
  staging: "https://auth.stg.foundations.di.no",
  production: "https://auth-login.di.no"
};
const cookieNames = {
  local: "localhost-auth-token",
  development: "dev-auth-token",
  staging: "stg-auth-token",
  production: "prod-auth-token"
};

class TokenValidator {
  /**
   * Returns the claim from the access token
   */
  getTokenClaim(token, key) {
    try {
      if (!token) {
        throw new Error(`Auth: Cannot access claim "${key}" from empty access token`);
      }
      const payload = jwtDecode(token);
      return payload[key];
    } catch (e) {
      console.error(e);
    }
  }
  _getTokenExpiryInDays(accessToken) {
    const expiresNumericDate = this.getTokenClaim(accessToken, "exp");
    if (!isDefined(expiresNumericDate)) {
      return void 0;
    }
    const currentDate = /* @__PURE__ */ new Date();
    const expiresAtDate = new Date(expiresNumericDate * 1e3);
    const timeDifference = expiresAtDate.getTime() - currentDate.getTime();
    return timeDifference / (1e3 * 60 * 60 * 24);
  }
  _isTokenExpired(token) {
    const expiresAtNumberOfDays = this._getTokenExpiryInDays(token);
    if (isDefined(expiresAtNumberOfDays) && expiresAtNumberOfDays <= 0) {
      console.error(new InvalidTokenError("Auth: accessToken is expired"));
      return true;
    }
    return false;
  }
  _isValidIssuer(token) {
    const issuer = this.getTokenClaim(token, "iss");
    if (!isDefined(issuer) || issuer !== SSO_ISSUER) {
      console.error(new InvalidTokenError("Auth: Invalid access token"));
      return false;
    }
    return true;
  }
  _validateToken(token) {
    return !this._isTokenExpired(token) && this._isValidIssuer(token);
  }
}

const isUrlLocalhost = (url) => url.includes("localhost");

var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var _appName, _loginUrl, _cookieName, _authBaseUrl, _usePkce, _currentUrl, _onError, _roles, _isInitialized, _isAuthenticated, _setCookiesWithPkce;
class AuthService extends TokenValidator {
  constructor(config) {
    super();
    __privateAdd(this, _appName);
    __privateAdd(this, _loginUrl);
    __privateAdd(this, _cookieName);
    __privateAdd(this, _authBaseUrl);
    __privateAdd(this, _usePkce);
    __privateAdd(this, _currentUrl);
    __privateAdd(this, _onError);
    __privateAdd(this, _roles);
    __privateAdd(this, _isInitialized, false);
    __privateAdd(this, _isAuthenticated, false);
    /**
     * Initialises the `AuthService` instance.
     *
     * Sets access token cookies and removes invalid tokens
     */
    __publicField(this, "init", async () => {
      try {
        if (this.accessToken && !this._validateToken(this.accessToken)) {
          this.logout();
          return true;
        }
        if (this.accessToken) {
          __privateSet(this, _isAuthenticated, true);
          return true;
        }
        if (__privateGet(this, _usePkce)) {
          const searchParams = __privateGet(this, _currentUrl).searchParams;
          const authCode = searchParams.get(AUTH_CODE_SEARCH_PARAM);
          if (authCode) {
            searchParams.delete(AUTH_CODE_SEARCH_PARAM);
            await __privateGet(this, _setCookiesWithPkce).call(this, authCode);
            __privateSet(this, _isAuthenticated, true);
            window.history.replaceState({}, "", __privateGet(this, _currentUrl).href);
          }
        }
        return true;
      } catch (e) {
        if (__privateGet(this, _onError)) {
          __privateGet(this, _onError).call(this, e);
        } else {
          console.error(e);
        }
        return false;
      } finally {
        __privateSet(this, _isInitialized, true);
      }
    });
    /**
     * Redirects the end-user to the login page.
     */
    __publicField(this, "login", () => {
      var _a;
      if (!__privateGet(this, _isInitialized)) {
        const notInitializedError = "Auth: Error logging in, Auth is not initialized";
        (_a = __privateGet(this, _onError)) == null ? void 0 : _a.call(this, new Error(notInitializedError));
        console.error(notInitializedError);
        return;
      }
      const currentUrl = encodeURI(window.location.href);
      const searchParams = new URLSearchParams(`redirectUrl=${currentUrl}&serviceName=${__privateGet(this, _appName)}`);
      if (__privateGet(this, _usePkce)) {
        searchParams.append("pkce", "true");
      }
      window.location.href = `${__privateGet(this, _loginUrl)}?${searchParams.toString()}`;
    });
    /**
     * Logout the current user. Clears the current access token.
     */
    __publicField(this, "logout", () => {
      var _a;
      if (!__privateGet(this, _isInitialized)) {
        const notInitializedError = "Auth: Error logging in, Auth is not initialized";
        (_a = __privateGet(this, _onError)) == null ? void 0 : _a.call(this, new Error(notInitializedError));
        console.error(notInitializedError);
        return;
      }
      if (this.accessToken) {
        this.clearToken();
        __privateSet(this, _isAuthenticated, false);
        return;
      }
      console.warn("Auth: User is already logged out");
    });
    /**
     * Clears access token using the `.di.no` domain
     */
    __publicField(this, "clearToken", () => {
      cookie.remove(__privateGet(this, _cookieName), {
        domain: isUrlLocalhost(__privateGet(this, _currentUrl).hostname) ? "localhost" : DEFAULT_DOMAIN
      });
    });
    __privateAdd(this, _setCookiesWithPkce, async (accessCode) => {
      const options = {
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        }
      };
      if (isUrlLocalhost(__privateGet(this, _currentUrl).hostname)) {
        options.headers = { ...options.headers, "X-Forwarded-For": "localhost" };
      }
      await new FetchHelper({ baseUrl: __privateGet(this, _authBaseUrl) }).post("/auth/static/sso/v2/otp/login", { otp: accessCode }, options);
    });
    if (!authEnvironments.includes(config.environment)) {
      throw new Error("Auth: Environment config is invalid.");
    }
    __privateSet(this, _appName, config.appName);
    __privateSet(this, _authBaseUrl, config.authBaseUrl);
    __privateSet(this, _onError, config.onError);
    __privateSet(this, _loginUrl, loginUrls[config.environment]);
    __privateSet(this, _cookieName, cookieNames[config.environment]);
    __privateSet(this, _usePkce, Boolean(config.usePkce));
    __privateSet(this, _currentUrl, new URL(window.location.href));
  }
  /**
   * Returns `true` if `AuthService` is properly initialized by `AuthService.init()`
   */
  get isInitialized() {
    return __privateGet(this, _isInitialized);
  }
  /**
   * Set the user's roles
   */
  set roles(roles) {
    __privateSet(this, _roles, roles);
  }
  /**
   * Returns the user's roles. Roles are set manually by using
   *
   * ```js
   * const auth = new AuthService({});
   * auth.roles = [];
   * ```
   */
  get roles() {
    return __privateGet(this, _roles);
  }
  /**
   * Returns the user's active access token
   */
  get accessToken() {
    return cookie.get(__privateGet(this, _cookieName));
  }
  /**
   * Returns the parsed JwtPayload from the access token
   */
  get accessTokenParsed() {
    var _a;
    if (!this.accessToken) {
      const emptyAccessTokenError = new Error("Auth: Access token returned empty, cannot parse token");
      (_a = __privateGet(this, _onError)) == null ? void 0 : _a.call(this, emptyAccessTokenError);
      throw emptyAccessTokenError;
    }
    return jwtDecode(this.accessToken);
  }
  /**
   * Returns a boolean if a token is expired.
   * If a token has no set expiration date, this returns `false`
   */
  get isTokenExpired() {
    if (!this.accessToken) {
      throw new Error("Auth: No access token available");
    }
    return this._isTokenExpired(this.accessToken);
  }
  /**
   * Returns `true` if the access token is available
   */
  get isAuthenticated() {
    return __privateGet(this, _isAuthenticated);
  }
}
_appName = new WeakMap();
_loginUrl = new WeakMap();
_cookieName = new WeakMap();
_authBaseUrl = new WeakMap();
_usePkce = new WeakMap();
_currentUrl = new WeakMap();
_onError = new WeakMap();
_roles = new WeakMap();
_isInitialized = new WeakMap();
_isAuthenticated = new WeakMap();
_setCookiesWithPkce = new WeakMap();

export { AUTH_CODE_SEARCH_PARAM, DEFAULT_DOMAIN, SSO_ISSUER, TokenValidator, authEnvironments, cookieNames, AuthService as default, isUrlLocalhost, loginUrls };
//# sourceMappingURL=index.mjs.map
